import { DataTable } from "primereact/datatable";
import {
  OrderConfigColumns,
} from "../../constants/table-columns/tableColumns";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { InputSwitch } from 'primereact/inputswitch';
import * as Yup from "yup";
import Spinner from "../../components/spinner/spinner";
import { Toast } from "primereact/toast";
import * as constants from "../../constants/constants";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import styles from "./OrderConfig.module.scss";
import { Dropdown } from "primereact/dropdown";
import { useFormik } from "formik";
import { appInsights } from "../../AppInsights";
import { getOrderConfigDetails, getOrderConfigTradingPartnerDetails, switchOrderFlow } from "../../services/OrderConfigService";

const OrderConfig = () => {

  const toast = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageRows, setCurrentPageRows] = useState(10);
  const [showSpinner, setShowSpinner] = useState(false);
  const [tradingPartners, setTradingPartners] = useState([]);
  const [regions, setRegions] = useState([]);
  const [OrderConfigData, setOrderConfigData] = useState([]);
  const [selectedOrderConfigData, setSelectedOrderConfigData] = useState({});
  const [orderConfigConfirmDialog, setOrderConfigConfirmDialog] = useState(false);

  const OrderConfigStatusFilterDropdownOptions = [
    { label: "All", value: null },
    { label: "Manual", value: "0" },
    { label: "Automated", value: "1" }
  ];

  const fetchOrderConfigDetails = async (formBody) => {
    try {
      setShowSpinner(true);
      formBody = {
        tradingPartnerName: formBody?.tradingPartnerName ? formBody?.tradingPartnerName : null,
        isAutomated: formBody?.isAutomated === "0" ? false : formBody?.isAutomated === "1" ? true
          : null,
        regionCode: formBody?.region ? formBody?.region : null
      };
      let result = await getOrderConfigDetails(formBody);
      setOrderConfigData(result);

    } catch (error) {
      setShowSpinner(false);
      setOrderConfigData([]);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    } finally {
      setShowSpinner(false);
    }
  };

  const ToggleBtn = (rowData) => {
    return (
      <div>
        <InputSwitch checked={rowData?.isAutomated} onChange={(e) => handleOrderConfigDialog(rowData)} className={styles?.switchProps} />
      </div>
    );
  };

  const initialValues = useMemo(
    () => ({
      tradingPartnerName: null,
      isAutomated: { label: "All", value: null },
      region: null
    }),
    []
  );

  const validationSchema = Yup.object({
    tradingPartnerName: Yup.string().nullable(),
    isAutomated: Yup.mixed().nullable(),
    region: Yup.mixed()
      .nullable(),
  });
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setCurrentPage(0);
      setCurrentPageRows(10);
      setShowSpinner(true);
      fetchOrderConfigDetails(values);
    },
    validateOnBlur: true,
    validateOnChange: true,
  });
  const fetchTradingPartnersList = async () => {
    try {
      setShowSpinner(true);
      const result = await getOrderConfigTradingPartnerDetails();
      setRegions(result);
    }
    catch (error) {
      setShowSpinner(false);
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo:
            "Error while Fetching Trading Partner list in order config Screen"
        },
        measurements: {
          severityLevel: 2
        }
      });
    } finally { setShowSpinner(false); }
  }

  const formRef = useRef(formik);
  const formikRef = useRef(formik);
  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);

  useEffect(() => {
    fetchTradingPartnersList();
    fetchOrderConfigDetails(formikRef.current?.values);
  }, []);

  const handleOrderConfigDialog = (rowData) => { setOrderConfigConfirmDialog(true); setSelectedOrderConfigData(rowData); }
  const approveOrRejectOrderConfigChange = async () => {
    try {

      let result = await switchOrderFlow(selectedOrderConfigData);
      if (!!result) {
        toast?.current?.show({
          severity: "success",
          summary: "Success",
          detail: result,
          life: 3000
        });
        fetchOrderConfigDetails({ ...formik?.values, regionCode: formik?.values?.region });
      }
    }
    catch (error) {
      setShowSpinner(false);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo:
            "Error while enable/disable automated order flow config setting"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
    finally { setSelectedOrderConfigData({}); }
  }

  const handleCloseConfirmDialog = useCallback(() => {
    setOrderConfigConfirmDialog(false);
  }, []);

  return (
    <>
      {showSpinner && <Spinner />}

      <ConfirmDialog
        visible={orderConfigConfirmDialog}
        onHide={handleCloseConfirmDialog}
        message={
          "Are you sure you want to change the staging configuration?"
        }
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={approveOrRejectOrderConfigChange}
        reject={handleCloseConfirmDialog}
      />

      <br></br>

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        <div className="grid p-fluid">
          <div className="col-12 md:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="Region">
                <span className={styles.formLabel}>Region</span>
              </label>
            </div>
            <Dropdown
              id="Region"
              filter
              showClear
              options={regions}
              placeholder="Region"
              optionLabel="region"
              optionValue="region"
              onChange={async (e) => {
                formik.setFieldValue("region", e?.value);
                formik.setFieldValue("tradingPartnerName", null);
                const selectedRegion = regions.find((x) => x?.region === e?.value);
                if (selectedRegion) {
                  setTradingPartners(selectedRegion?.tradingPartners);
                } else {
                  setTradingPartners([]);
                }

              }}
              value={formik?.values?.region}
              className={styles.dropdown}
              onBlur={formik.handleBlur}
            />
          </div>

          <div className="col-12 md:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="tradingPartnerName">
                <span className={styles.formLabel}>Trading Partner</span>
              </label>
            </div>
            <Dropdown
              id="tradingPartnerName"
              filter
              showClear
              placeholder="Trading Partner"
              optionLabel="tradingPartnerDisplayName"
              optionValue="tradingPartnerName"
              options={tradingPartners}
              onChange={async (e) => {
                formik.setFieldValue("tradingPartnerName", e?.value);
              }}
              value={formik?.values?.tradingPartnerName}
              className={styles?.dropdown}
              onBlur={formik.handleBlur}
            />
            {/* {formik.touched.tradingPartnerName && formik.errors.tradingPartnerName && (
              <small className="p-error">{formik.errors.tradingPartnerName}</small>
            )} */}

          </div>
          <div className="col-12 md:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="isAutomated">
                <span className={styles?.formLabel}>Order Flow</span>
              </label>
            </div>
            <Dropdown
              id="isAutomated"
              placeholder="Select Order Flow"
              value={formik.values?.isAutomated ?? null}
              className={styles.dropdown}
              options={OrderConfigStatusFilterDropdownOptions}
              onChange={(e) => {
                formik.setFieldValue("isAutomated", e?.value ?? null);
              }}
            />
          </div>

          <div className="col-12 md:col-6 gap-3" style={{ marginTop: "2.5rem" }}>
            <div className="grid">
              <div className="col-12 md:col-3 ">
                <Button
                  label="Search"
                  type="submit"
                  size="medium"
                  className={styles?.primaryBtn}
                />
              </div>
              <div className="col-12 md:col-3 ">
                <Button
                  label="Reset"
                  type="button"
                  size="medium"
                  onClick={() => {
                    setSelectedOrderConfigData([]);
                    if (!regions[0]?.region) {
                      return;
                    }
                    setTradingPartners([]);
                    setRegions(regions);
                    formik.setValues({
                      tradingPartnerName: null,
                      isAutomated: { label: "All", value: null },
                      region: null,
                    });
                    fetchOrderConfigDetails({
                      tradingPartnerName: null,
                      isAutomated: { label: "All", value: null },
                      regionCode: null,
                    });
                  }}
                  className={styles?.resetBtn}
                />
              </div>
            </div>

          </div>
        </div>
      </form>

      <br></br>

      <DataTable
        value={OrderConfigData}
        tableStyle={{ minWidth: "50rem" }}
        scrollable
        first={currentPage}

        rows={currentPageRows}
        onPage={(e) => { setCurrentPage(e.first); setCurrentPageRows(e?.rows) }}
        paginator={constants?.paginator}
        rowsPerPageOptions={constants?.rowsPerPageOptions}
        paginatorTemplate={constants?.paginatorTemplate}
        currentPageReportTemplate={constants?.currentPageReportTemplate}
        emptyMessage={constants?.emptyMessage}
        dataKey="id"
        scrollHeight="30vw"
      >
        {OrderConfigColumns?.map((col, index) => (
          <Column
            key={index}
            field={col.field}
            header={col.header}
            sortable={col.sort}
            style={{ minWidth: col.minWidth }}
            body={
              col.field.toLocaleLowerCase() === "action"
                ? ToggleBtn
                : col.value
            }
          />
        ))}
      </DataTable>

      <Toast ref={toast} position="center" />
    </>
  );
};

export default OrderConfig;